import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const newOperationId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `asset-reverse-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const AssetPurchaseCancelDialog = ({ open, onOpenChange, purchase, onSaved }) => {
  const [reason, setReason] = useState("");
  const [operationId, setOperationId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setOperationId(newOperationId());
    }
  }, [open, purchase?.id]);

  const reverse = async () => {
    if (reason.trim().length < 3) {
      toast.error("A reversal reason is required");
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.post(`/assets/purchases/${purchase.id}/cancel`, {
        cancellation_reason: reason.trim(),
        operation_id: operationId,
        expected_version: Number(purchase.version || 0),
      });
      toast.success("Purchase reversed through the audited workflow");
      onSaved(data);
      onOpenChange(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="asset-purchase-reverse-dialog">
        <DialogHeader>
          <DialogTitle data-testid="asset-purchase-reverse-title">
            Reverse Purchase {purchase?.number || ""}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-600" data-testid="asset-purchase-reverse-notice">
          This reverses the approved receipt only when the stock has not been used or moved.
        </div>
        <div>
          <Label>Reversal Reason *</Label>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="mt-1"
            data-testid="asset-purchase-reverse-reason"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="asset-purchase-reverse-cancel">
            Keep Purchase
          </Button>
          <Button
            onClick={reverse}
            disabled={saving}
            className="bg-red-700 text-white hover:bg-red-800"
            data-testid="asset-purchase-reverse-submit"
          >
            {saving ? "Reversing…" : "Reverse Purchase"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};